import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import SuiteFeatureTableBadge from 'pricing-pages-lib/components/SuiteFeatureTableBadge';
import { getButtonVariantProps, getTrellisButtonVariantProps } from 'pricing-pages-lib/utils/buttonVariantUtils';
import { useSelector } from 'react-redux';
import { FREE, STARTER } from 'self-service-api/constants/ProductLevels';
import { SUITE_FEATURE_TABLE_HEADER } from '../../../constants/tracking/TrackingSources';
import { SuiteCTA } from '../../../features/purchaseMotions/components/SuiteCTA';
import { TalkToSalesFallback } from '../../../features/purchaseMotions/components/TalkToSalesFallback';
import useSuitePurchaseMotions from '../../../features/purchaseMotions/hooks/useSuitePurchaseMotions';
import { useGlobalLoading } from '../../../hooks/loadingHooks';
import { useAppSelector } from '../../../reducers';
import { isAppAuthSelector } from '../../../selectors/AuthSelectors';
import { getIsLatestVersionOfCrmSuiteInProductInventoryPreSeatsAndSeatsOnlySelector, getIsLatestVersionOfProductInProductInventorySelector } from '../../../selectors/ProductInventorySelectors';
import { currentSuiteSkuIdsForProductLevelSelector, getTBSuiteProductForProductLevelSelector, hasAnyTBSkuInProductDataSelector } from '../../../selectors/ProductsSelectors';
import CodeSplitEnvWrapper from '../../common/env/CodeSplitEnvWrapper';
import SkeletonLoadWrapper from '../../common/loading/SkeletonLoadWrapper';
import ErrorBoundary from '../../error-boundary/ErrorBoundary';
import CrmSuitePriceFeatureTable from '../../merch-card/price-display/CrmSuitePriceFeatureTable';
import FeatureTableFreePriceDisplay from '../FeatureTableFreePriceDisplay';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SuiteFeatureTableHeaderCell = ({
  productLevel,
  isFreeToStarter,
  screen
}) => {
  const isAppReady = useGlobalLoading();
  const isFree = productLevel === FREE;
  const isCurrentPlanLevel = useSelector(state => {
    if (!isAppReady || isFree) {
      return false;
    }
    const isBundleSku = hasAnyTBSkuInProductDataSelector(state);
    if (isBundleSku) {
      const suiteProduct = getTBSuiteProductForProductLevelSelector(state, {
        suiteProductLevel: productLevel
      });
      return suiteProduct && getIsLatestVersionOfProductInProductInventorySelector(state, {
        productIdentifier: {
          apiName: suiteProduct.apiName
        }
      }) || false;
    }
    return getIsLatestVersionOfCrmSuiteInProductInventoryPreSeatsAndSeatsOnlySelector(state, productLevel);
  });
  const isAppAuth = useSelector(isAppAuthSelector);
  let isHighlightedCell = STARTER === productLevel;
  if (!isFreeToStarter) {
    isHighlightedCell = isCurrentPlanLevel;
  }
  const purchaseMotions = useSuitePurchaseMotions({
    // fall back to [] if not found
    productLevel: productLevel,
    returnFirstCTAOnly: true
  });
  const ctaVariantProps = getButtonVariantProps({
    variant: 'table-header'
  });
  const trellisCtaVariantProps = getTrellisButtonVariantProps({
    variant: 'table-header',
    visible: isCurrentPlanLevel
  });
  const skuIds = useAppSelector(state => {
    if (!isAppReady) return [];
    if (productLevel === FREE) return [];
    return currentSuiteSkuIdsForProductLevelSelector(state, {
      productLevel
    });
  });
  const suiteSkuId = (skuIds === null || skuIds === void 0 ? void 0 : skuIds.length) === 1 ? skuIds[0] : null;
  return /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "column",
    alignItems: "center",
    children: [isHighlightedCell && /*#__PURE__*/_jsx(View, {
      insetBlockStart: "400",
      insetInlineStart: "0",
      insetInlineEnd: "0",
      marginBlock: "0",
      marginInline: "auto",
      inlineSize: "fit-content",
      position: "absolute",
      children: /*#__PURE__*/_jsx(SuiteFeatureTableBadge, {
        use: isFreeToStarter ? 'new' : 'koala',
        translationKey: isFreeToStarter ? isAppAuth ? 'recommended' : 'mostPopular' : 'currentPlan'
      })
    }), /*#__PURE__*/_jsx(Text, {
      as: "strong",
      typography: "heading-50",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `products.crm.${productLevel}`
      })
    }), /*#__PURE__*/_jsx(View, {
      paddingBlock: "100",
      children: isFree ? /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !isAppReady,
        heights: [20],
        widths: [140],
        children: /*#__PURE__*/_jsx(FeatureTableFreePriceDisplay, {})
      }) : /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !isAppReady,
        heights: [20],
        widths: [140],
        children: /*#__PURE__*/_jsx(CrmSuitePriceFeatureTable, {
          productLevel: productLevel
        })
      })
    }), isFree ? /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
      isLoading: !isAppReady,
      heights: [40],
      widths: [140],
      children: /*#__PURE__*/_jsx(View, Object.assign({}, trellisCtaVariantProps, {
        children: /*#__PURE__*/_jsx(CodeSplitEnvWrapper, {
          authedComponentLoader: () => import( /* webpackChunkName: "authed-chunk" */`pricing-pages-lib/buttons/CurrentPlanButton`),
          publicComponentLoader: () => import( /* webpackChunkName: "public-chunk" */`pricing-pages-ui/components/common/SignupButton`),
          authedComponentProps: Object.assign({
            visible: trellisCtaVariantProps.visible
          }, ctaVariantProps),
          publicComponentProps: Object.assign({
            sourceId: SUITE_FEATURE_TABLE_HEADER,
            productLine: 'crm',
            use: 'primary'
          }, ctaVariantProps, {
            trackingSource: SUITE_FEATURE_TABLE_HEADER
          })
        })
      }))
    }) : /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
      isLoading: !isAppReady,
      heights: [40],
      widths: [140],
      children: /*#__PURE__*/_jsx(View, Object.assign({}, trellisCtaVariantProps, {
        children: /*#__PURE__*/_jsx(ErrorBoundary, {
          fallback: suiteSkuId ? /*#__PURE__*/_jsx(TalkToSalesFallback, {
            skuId: suiteSkuId,
            trackingSource: SUITE_FEATURE_TABLE_HEADER,
            screen: screen
          }) : null,
          children: purchaseMotions.map(purchaseMotion => /*#__PURE__*/_jsx("div", {
            children: /*#__PURE__*/_jsx(SuiteCTA, Object.assign({
              purchaseMotion: purchaseMotion,
              productLevel: productLevel,
              trackingSource: SUITE_FEATURE_TABLE_HEADER,
              screen: screen,
              purchaseMotionLocation: 'GENERIC' // replace with FEATURE_TABLE when available https://git.hubteam.com/HubSpot/pricing-pages-unified-ui/issues/11974
            }, ctaVariantProps))
          }, purchaseMotion))
        })
      }))
    })]
  });
};
export default SuiteFeatureTableHeaderCell;